// import Apple from "@/assets/icons/Apple.svg";
import GoogleIcon from "@/assets/icons/Google.svg";
import { CustomButton } from "@/components/ui/Buttons";
import HookFormInputField from "@/components/form/HookFormInputField";
import { FormProvider, useForm } from "react-hook-form";
// import { CustomCheckbox } from "@/components/ui/Checkbox";
import {
  Link,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  LoginFormValues,
  loginWithEmailSchema,
} from "@/validation/form.schema";
import { useLoginMutation } from "@/redux/feature/auth/authApi";
import { useAppDispatch } from "@/redux/hooks/hooks";
import { useState } from "react";
import { decodeAccessTokenIfValid } from "@/utils/verifyToken";
import { getRedirectForm } from "@/utils/redirectManager";
import { setUser } from "@/redux/feature/auth/authSlice";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import logo from "@/assets/logo/logo-icon.png";

const SignInV2 = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerms] = useSearchParams();
  const userExists = searchTerms.get("userNotExist");
  const clientAssociated = searchTerms.get("clientAssociated");
  const [login] = useLoginMutation();
  const methods = useForm<LoginFormValues>({
    resolver: zodResolver(loginWithEmailSchema),
  });

  const navigate = useNavigate();
  const location = useLocation();
  const redirect = getRedirectForm(location.search);

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setLoading(true);
      setError("");

      const payload = {
        email: data.email.trim().toLowerCase(),
        password: data.password,
      };

      const res = await login(payload).unwrap();

      const token = res?.data?.accessToken;
      const user = (
        typeof token === "string" ? decodeAccessTokenIfValid(token) : null
      ) as { email?: string; role?: string } | null;

      // Apply the dashboard's token and role checks before storing the session
      // or reporting success, otherwise ProtectedRoute immediately logs out.
      if (!user || typeof user.email !== "string" || !user.email.trim()) {
        throw new Error(
          "Your login session is invalid or expired. Please try logging in again.",
        );
      }
      if (user.role === "client") {
        throw new Error(
          "This email is associated with a client account. Please log in using the client dashboard.",
        );
      }
      if (user.role !== "creator") {
        throw new Error(
          "This account does not have access to the creator dashboard.",
        );
      }

      dispatch(setUser({ user, token }));
      navigate(redirect, { replace: true });
      toast.success("Successfully Logged In");
    } catch (err) {
      const apiError = err as {
        data?: { message?: string; errorSource?: { message?: string }[] };
      };
      const message =
        (err instanceof Error ? err.message : undefined) ||
        apiError?.data?.errorSource?.[0]?.message ||
        apiError?.data?.message ||
        "Unable to log in. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const api_baseUrl =
        import.meta.env.VITE_NODE_ENV === "development"
          ? import.meta.env.VITE_API_BASE_URL_LOCAL
          : import.meta.env.VITE_NODE_ENV === "stage"
            ? import.meta.env.VITE_API_BASE_URL_STAGE
            : import.meta.env.VITE_NODE_ENV === "dev"
              ? import.meta.env.VITE_API_BASE_URL_DEV
              : import.meta.env.VITE_API_BASE_URL_PROD;

      const redirectUrl = `${window.location.origin}/auth/verify?redirect=${encodeURIComponent(
        redirect,
      )}`;
      const base_url = `${window.location.origin}`;

      window.location.href = `${api_baseUrl}/auth/google-oauth?redirectTo=${encodeURIComponent(
        redirectUrl,
      )}&baseUrl=${encodeURIComponent(base_url)}&state=creator_login`;
    } catch (error) {
      console.error("Error during Google sign-in:", error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8 lg:px-0">
      <p className="mb-3 w-full max-w-[575px] text-right text-xs leading-4 text-black">
        Managing a business?{" "}
        <a
          className="text-[#FF6600] hover:underline"
          href="https://passionbits.io/dashboard/auth/sign-in"
        >
          Login as a Client
        </a>
      </p>
      <section className="border border-[#D8D8DD] w-full max-w-[575px] p-4 sm:p-6 rounded-2xl bg-white">
        <div className="border-[1.5px] rounded-lg border-gray-300 p-1 w-[40px]">
          <img src={logo} className="w-full" alt="Logo" />
        </div>
        <h2 className="text-gray-900 pb-2 text-heading-4 font-semibold mt-3">
          Login
        </h2>
        <p className="text-gray-500 select-none text-caption-1-large">
          Welcome back! Enter the details.
        </p>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="mt-8 space-y-5"
          >
            <HookFormInputField
              placeholder="josephwilliams@gmail.com"
              name="email"
              label="Email"
            />

            <HookFormInputField
              placeholder="Enter password"
              name="password"
              label="Password"
              type="password"
            />

            <div className="flex justify-end">
              <Link
                className="text-sm font-semibold text-[#FF6600]"
                to="/auth/forgot-password"
              >
                Forgot password?
              </Link>
            </div>

            {userExists === "true" && (
              <p className="text-sm text-red-600 leading-[14px]">
                User does not exist. Please create an account first.
              </p>
            )}

            {clientAssociated === "true" && (
              <p className="text-sm text-red-600 leading-[14px]">
                This email is already associated with a client account.
              </p>
            )}

            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}

            {/* Primary Button (Matched Style) */}
            <CustomButton
              backgroundColor="#FF6600"
              color="white"
              padding="10px"
              fontSize="16px"
              fontWeight="550"
              borderRadius="200px"
              className="w-full auth-btn-shadow"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Log In"}
            </CustomButton>

            {/* Google Button (Matched Style) */}
            <CustomButton
              variant="outline"
              color="#344054"
              padding="10px"
              fontSize="16px"
              fontWeight="550"
              borderRadius="200px"
              border="1px solid #D0D5DD"
              className="w-full flex justify-center items-center gap-x-3 auth-btn-shadow"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <GoogleIcon />
              <span>Sign in with Google</span>
            </CustomButton>
          </form>
        </FormProvider>

        <div className="flex items-center gap-x-1 mt-8 justify-center">
          <p className="text-[#475467] text-sm tracking-normal">
            Don’t have an account?
          </p>
          <Link
            to={"/auth/sign-up"}
            className="text-[#FF6600] text-sm tracking-normal font-semibold"
          >
            Create Account
          </Link>
        </div>

        <ScrollRestoration />
      </section>
    </div>
  );
};

export default SignInV2;
